import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Rooted",
    body: "Born in Paterson, sharpened in Norfolk. Every bar is stitched from lived streets — a testimony that refuses to be edited for comfort.",
  },
  {
    n: "02",
    title: "Rebellion",
    body: "Music as protest, prayer, and permission. Songs that push against the grain of easy listening and hold space for the people the industry forgets.",
  },
  {
    n: "03",
    title: "Reverence",
    body: "Faith is not decoration here — it is the frame. Each record is offered like an altar: honest, human, and unafraid of what the light might reveal.",
  },
  {
    n: "04",
    title: "Reach",
    body: "For the block, the pulpit, and everything between. The mission is legacy — a sound handed down, not sold out.",
  },
];

export const Manifesto = () => {
  const { scrollYProgress } = useScroll();
  const lineWidth = useTransform(scrollYProgress, [0.35, 0.55], ["0%", "100%"]);

  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#050505] overflow-hidden"
    >
      <div
        className="spotlight"
        style={{
          width: 700,
          height: 700,
          background: "rgba(245,158,11,0.06)",
          top: "20%",
          right: "-250px",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-20"
        >
          <div className="flex items-center gap-4 mb-5">
            <span className="h-px w-10 bg-[color:var(--gold)]" />
            <span className="overline">The Manifesto</span>
          </div>
          <h2 className="font-condensed text-5xl sm:text-6xl lg:text-8xl leading-[0.9]">
            Four <span className="font-display italic gold-text">movements.</span>
            <br />
            One truth.
          </h2>
        </motion.div>

        <div className="relative">
          {/* Vertical gold hairline that fills on scroll */}
          <motion.div
            style={{ height: lineWidth }}
            className="hidden md:block absolute left-[calc(6rem_-_1px)] top-0 w-px bg-gradient-to-b from-[color:var(--gold)] to-transparent"
            aria-hidden
          />

          <ol className="space-y-16 sm:space-y-24">
            {CHAPTERS.map((c, i) => (
              <motion.li
                key={c.n}
                data-testid={`manifesto-chapter-${i}`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{
                  duration: 0.9,
                  delay: i * 0.05,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="grid md:grid-cols-[6rem_1fr] gap-8 md:gap-16 items-start group"
              >
                <div className="relative">
                  <span className="font-display text-6xl sm:text-7xl md:text-8xl gold-text tracking-tighter block leading-none transition-transform duration-700 group-hover:-translate-y-1">
                    {c.n}
                  </span>
                  <span className="hidden md:block absolute left-full top-6 h-px w-8 bg-white/15" />
                </div>
                <div>
                  <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-4 tracking-tight">
                    {c.title}
                  </h3>
                  <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
                    {c.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
